import { Hub, HubType } from '@alucio/aws-beacon-amplify/src/models';
import { DNAButton, DNAContextMenu, DNATextProps, DNAIconStyleProps } from '@alucio/lux-ui';
import React from 'react';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider';
import { useCurrentPage } from '../DNA';
import { ROUTES } from 'src/router/routes';

interface HubsContextMenuProps {
  hub: Hub,
}

type HubContextOptions = {
  [key in keyof typeof HubContextMenuActions]: {
    title: string
    icon: string
    onPress: () => void
    status?: DNATextProps['status'],
    iconStatus?: DNAIconStyleProps['status']
  }
}

type HubActions = (keyof typeof HubContextMenuActions)

enum HubContextMenuActions {
  edit = 'Edit',
  preview = 'Preview',
  share = 'Share',
  delete = 'Delete',
  duplicate = 'Duplicate',
}

const HubsContextMenu: React.FC<HubsContextMenuProps> = (props) => {
  const { isOnline, deviceMode } = useAppSettings();
  const { handleEditHub, handlePressShare, openPreviewWindow, handleDeleteHub, handleDuplicateHub } = useHubsState();
  const { hub } = props;
  const { id } = hub;
  const isTablet = deviceMode === 'tablet';
  const route = useCurrentPage()
  const isPublisherRoute = route?.PATH === ROUTES.UI2_PUBLISHER_HUB_TEMPLATES.PATH

  const menuActions: HubContextOptions = {
    edit: {
      title: 'Edit',
      icon: 'pencil-simple',
      onPress: () => handleEditHub(id)
    },
    preview: {
      title: 'Preview',
      icon: 'eye',
      onPress: () => openPreviewWindow(id)
    },
    duplicate: {
      title: 'Duplicate',
      icon: 'copy',
      onPress: () => handleDuplicateHub(hub)
    },
    share: {
      title: 'Share',
      icon: 'envelope-simple',
      onPress: () => handlePressShare(id)
    },
    delete: {
      title: 'Delete',
      icon: 'trash',
      onPress: () => handleDeleteHub(hub),
      status: 'danger',
      iconStatus: 'danger',
    }
  }

  const actionsToUse: HubActions[] = ['edit']
  isOnline && actionsToUse.push('preview')
  !isPublisherRoute && actionsToUse.push('duplicate')
  isOnline && actionsToUse.push('share')

  // since templates are not deletable, we need to check the hub type
  if (hub.hubType !== HubType.TEMPLATE) {
    actionsToUse.push('delete')
  }

  return (
    <DNAContextMenu>
      <DNAContextMenu.Anchor>
        <DNAButton
          appearance="outline"
          status="tertiary"
          size={isTablet ? 'xl' : 'md'}
          iconRight="dots-three-vertical-bold"
        />
      </DNAContextMenu.Anchor>
      <DNAContextMenu.Items>
        { actionsToUse.map(item => {
          return (
            <DNAContextMenu.Item
              key={menuActions[item].title}
              icon={menuActions[item].icon}
              title={menuActions[item].title}
              onPress={menuActions[item].onPress}
              status={menuActions[item].status}
              iconStatus={menuActions[item].iconStatus}
            />
          )
        })}
      </DNAContextMenu.Items>
    </DNAContextMenu>
  )
}

export default HubsContextMenu
